import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import MessagesView from './MessagesView';
import MyAttendance from './MyAttendance';
import MyTasks from './MyTasks';

const EmployeeDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser } = useAuth();
  const { tasks, attendance, checkIn, checkOut, loading } = useData();

  const employeeMenuItems = [
    { name: 'Overview', path: '/dashboard', icon: 'home' },
    { name: 'My Tasks', path: '/dashboard/tasks', icon: 'check-square' },
    { name: 'Attendance', path: '/dashboard/attendance', icon: 'clock' },
    { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' }
  ];

  const myTasks = tasks.filter(t => t.assigneeId === currentUser?.id);
  const myAttendance = attendance.filter(a => a.userId === currentUser?.id);
  const today = new Date().toISOString().split('T')[0];
  const todayAttendance = attendance.find(record => 
    (record.userId === currentUser?.id || record.userId === currentUser?.uid) && 
    record.date === today
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const hasCheckedIn = todayAttendance && todayAttendance.checkIn;
  const hasCheckedOut = todayAttendance && todayAttendance.checkOut;

  const DashboardHome = () => {
    // ADDED: Real-time timer functionality
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentWorkHours, setCurrentWorkHours] = useState(0);

    useEffect(() => {
      // Update current time every second
      const timer = setInterval(() => {
        setCurrentTime(new Date());
        
        // Calculate current work hours if checked in but not out
        if (todayAttendance?.checkIn && !todayAttendance?.checkOut) {
          const checkInTime = new Date(`${today} ${todayAttendance.checkIn}`);
          const now = new Date();
          const diffInMs = now - checkInTime;
          const diffInHours = diffInMs / (1000 * 60 * 60);
          setCurrentWorkHours(Math.max(0, diffInHours));
        }
      }, 1000);

      return () => clearInterval(timer);
    }, [todayAttendance, today]);

    // Handle check in
    const handleCheckIn = async () => {
      if (!hasCheckedIn && !loading) {
        try {
          await checkIn();
        } catch (error) {
          console.error('Check-in failed:', error);
        }
      }
    };

    // Handle check out
    const handleCheckOut = async () => {
      if (hasCheckedIn && !hasCheckedOut && !loading) {
        try {
          await checkOut();
        } catch (error) {
          console.error('Check-out failed:', error);
        }
      }
    };

    return (
      <div className="dashboard-content">
        <div className="dashboard-headerr">
          <h2>Welcome back, {currentUser?.name}!</h2>
          <p>Stay productive and manage your tasks efficiently.</p>
        </div>

        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-tasks"></i>
            </div>
            <div className="empinfo">
              <h3>{myTasks.length}</h3>
              <p>Total Tasks</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="empinfo">
              <h3>{myTasks.filter(t => t.status === 'completed').length}</h3>
              <p>Completed</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="empinfo">
              <h3>{myTasks.filter(t => t.status === 'ongoing').length}</h3>
              <p>In Progress</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-calendar-check"></i>
            </div>
            <div className="empinfo">
              <h3>{myAttendance.length}</h3>
              <p>Days Worked</p>
            </div>
          </div>
        </div>

        {/* UPDATED: Compact Attendance Component */}
        <div className="attendance-section">
          <div className="attendance-card-compact">
            <div className="attendance-header-compact">
              <h3>Today's Attendance</h3>
              <div className="current-time">
                {currentTime.toLocaleDateString()} - {currentTime.toLocaleTimeString()}
              </div>
            </div>
            
            <div className="attendance-content-compact">
              {/* Status Items in Row */}
              <div className="status-row">
                <div className="status-item-compact">
                  <div className="status-icon-small check-in">
                    <i className="fas fa-sign-in-alt"></i>
                  </div>
                  <div className="status-info-compact">
                    <span className="status-label-small">Check In</span>
                    <span className="status-time-small">
                      {todayAttendance?.checkIn ? todayAttendance.checkIn.substring(0, 5) : '--:--'}
                    </span>
                  </div>
                </div>

                <div className="status-item-compact">
                  <div className="status-icon-small check-out">
                    <i className="fas fa-sign-out-alt"></i>
                  </div>
                  <div className="status-info-compact">
                    <span className="status-label-small">Check Out</span>
                    <span className="status-time-small">
                      {todayAttendance?.checkOut ? todayAttendance.checkOut.substring(0, 5) : '--:--'}
                    </span>
                  </div>
                </div>

                <div className="status-item-compact">
                  <div className="status-icon-small hours">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="status-info-compact">
                    <span className="status-label-small">Work Hours</span>
                    <span className="status-time-small">
                      {todayAttendance?.checkOut ? 
                        `${todayAttendance.hoursWorked?.toFixed(1) || 0}h` : 
                        (todayAttendance?.checkIn ? `${currentWorkHours.toFixed(1)}h` : '0h')
                      }
                    </span>
                  </div>
                </div>

                {/* Action Buttons in Same Row */}
                <div className="action-buttons-row">
                  <button
                    className="btn-compact btn-success-compact"
                    onClick={handleCheckIn}
                    disabled={loading || hasCheckedIn}
                  >
                    {loading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : hasCheckedIn ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <i className="fas fa-sign-in-alt"></i>
                    )}
                    {hasCheckedIn ? 'Checked In' : 'Check In'}
                  </button>

                  <button
                    className="btn-compact btn-warning-compact"
                    onClick={handleCheckOut}
                    disabled={loading || !hasCheckedIn || hasCheckedOut}
                  >
                    {loading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : hasCheckedOut ? (
                      <i className="fas fa-check"></i>
                    ) : !hasCheckedIn ? (
                      <i className="fas fa-times"></i>
                    ) : (
                      <i className="fas fa-sign-out-alt"></i>
                    )}
                    {hasCheckedOut ? 'Checked Out' : 
                     !hasCheckedIn ? 'Check In First' : 'Check Out'}
                  </button>
                </div>
              </div>

              {/* Live Timer (only if checked in but not out) */}
              {hasCheckedIn && !hasCheckedOut && (
                <div className="work-timer-compact">
                  <i className="fas fa-stopwatch"></i>
                  <span>Working: {Math.floor(currentWorkHours)}h {Math.floor((currentWorkHours % 1) * 60)}m</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>My Active Tasks</h3>
            <div className="task-list">
              {myTasks.filter(t => t.status !== 'completed').slice(0, 5).map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-info">
                    <h4>{task.title}</h4>
                    <p>Due: {new Date(task.dueDate).toLocaleDateString()}</p>
                  </div>
                  <div className="task-meta">
                    <span className={`priority-badge priority-${task.priority}`}>
                      {task.priority}
                    </span>
                    <span className={`status-badge status-${task.status}`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
              {myTasks.filter(t => t.status !== 'completed').length === 0 && (
                <p className="no-data">No active tasks</p>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Recent Activity</h3>
            <div className="activity-list">
              {myTasks.slice(0, 3).map(task => (
                <div key={task.id} className="activity-item">
                  <div className="activity-icon">
                    <i className="fas fa-tasks"></i>
                  </div>
                  <div className="activity-content">
                    <p>Task "{task.title}" was assigned to you</p>
                    <span className="activity-time">
                      {new Date(task.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
              {myTasks.length === 0 && (
                <p className="no-data">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={employeeMenuItems}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title="Employee Dashboard"
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/tasks" element={<MyTasks />} />
            <Route path="/attendance" element={<MyAttendance />} />
            <Route path="/messages" element={<MessagesView />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
